// Chart primitives, hand drawn on the tokens (ZENITH.md §18). The rules the
// prototype settled, applied once here rather than per widget: marks are
// thin with rounded ends and a surface gap between adjacent fills; values
// and labels wear ink, never the series colour, with a swatch carrying
// identity, so text stays legible on both surfaces; every plotted mark has
// a tooltip; categorical charts carry direct labels, which is the documented
// relief for the light theme's contrast on three of the eight slots.
import { useState, type PointerEvent, type ReactNode } from "react";
import { seriesColor } from "./format";

interface Tip { x: number; y: number; content: ReactNode }

function useTooltip() {
  const [tip, setTip] = useState<Tip>();
  const show = (e: PointerEvent<HTMLElement>, content: ReactNode) => {
    const host = e.currentTarget.closest(".chart-host");
    if (!host) return;
    const r = host.getBoundingClientRect();
    setTip({ x: e.clientX - r.left, y: e.clientY - r.top, content });
  };
  const hide = () => setTip(undefined);
  const node = tip ? <div role="tooltip" className="tip" style={{ left: tip.x, top: tip.y }}>{tip.content}</div> : null;
  return { show, hide, node };
}

export function ChartHost({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={"chart-host" + (className ? " " + className : "")}>{children}</div>;
}

export type Tone = "good" | "warn" | "serious" | "bad";
const TONE_VAR: Record<Tone, string> = { good: "var(--status-good)", warn: "var(--status-warning)", serious: "var(--status-serious)", bad: "var(--status-critical)" };

/** One big number with its unit, label and a line of context. */
export function Stat({ value, unit, label, sub, tone }: { value: ReactNode; unit?: string; label?: ReactNode; sub?: ReactNode; tone?: Tone }) {
  return (
    <div className="stat">
      <div className="stat-row">
        <span className="stat-value" style={tone ? { color: TONE_VAR[tone] } : undefined}>{value}</span>
        {unit && <span className="stat-unit">{unit}</span>}
      </div>
      {label && <div className="stat-label">{label}</div>}
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  );
}

export interface Segment { key: string; label: string; value: number; color: string }

/** One horizontal bar split into named parts: a share of a whole with a
 * handful of parts, such as signal quality or the band split. */
export function SegmentBar({ segments, legend = true, height = 10, onSelect, selected }: {
  segments: Segment[]; legend?: boolean; height?: number;
  /** Given, every legend entry becomes a control that narrows to its part, and
   * the legend stops being only a key to the colours. Optional so the bars that
   * are a reading and not a filter — the dashboard's — render exactly as before.
   * The caller decides what selecting means and toggles `selected` itself. */
  onSelect?(key: string): void;
  /** The part currently narrowed to, if any. */
  selected?: string;
}) {
  const { show, hide, node } = useTooltip();
  const total = segments.reduce((a, s) => a + s.value, 0);
  if (total === 0) return <div className="segbar-empty" style={{ height }} />;
  return (
    <ChartHost>
      <div className="segbar" style={{ height }} role="img" aria-label={segments.map((s) => `${s.label} ${s.value}`).join(", ")}>
        {segments.filter((s) => s.value > 0).map((s) => (
          <div key={s.key} className="segbar-part" data-key={s.key} style={{ width: `${(s.value / total) * 100}%`, background: s.color }}
            onPointerMove={(e) => show(e, `${s.label}: ${s.value.toLocaleString()} (${Math.round((s.value / total) * 100)}%)`)} onPointerLeave={hide} />
        ))}
      </div>
      {legend && (
        <div className="legend">
          {segments.map((s) => {
            const body = (
              <>
                <span className="swatch" style={{ background: s.color }} />
                {s.label}
                <b>{s.value.toLocaleString()}</b>
              </>
            );
            return onSelect ? (
              <button key={s.key} type="button" className="legend-item legend-pick" aria-pressed={selected === s.key}
                onClick={() => onSelect(s.key)}>{body}</button>
            ) : (
              <span key={s.key} className="legend-item">{body}</span>
            );
          })}
        </div>
      )}
      {node}
    </ChartHost>
  );
}

export interface BarRow { key: string; label: ReactNode; text?: string; value: number; color?: string; hint?: string; aside?: ReactNode }

/** Ranked horizontal bars with the label and value in ink. The right form for
 * "which of these is biggest" over text labels; a pie of eight SSIDs is not. */
export function BarList({ rows, colorBy = "single", format = (v) => v.toLocaleString(), empty = "No data yet." }: {
  rows: BarRow[]; colorBy?: "single" | "series"; format?(v: number): string; empty?: string;
}) {
  const { show, hide, node } = useTooltip();
  if (rows.length === 0) return <p className="muted">{empty}</p>;
  const max = Math.max(...rows.map((r) => r.value), 1);
  return (
    <ChartHost>
      <ol className="barlist">
        {rows.map((r, i) => {
          const color = r.color ?? (colorBy === "series" ? seriesColor(i) : "var(--seq-400)");
          return (
            <li key={r.key} className="barlist-row" onPointerMove={(e) => show(e, `${r.text ?? String(r.label)}: ${format(r.value)}${r.hint ? " · " + r.hint : ""}`)} onPointerLeave={hide}>
              <div className="barlist-head">
                <span className="barlist-label">{colorBy === "series" && <span className="swatch" style={{ background: color }} />}{r.label}</span>
                <span className="barlist-value">{r.aside}<b>{format(r.value)}</b></span>
              </div>
              <div className="barlist-track"><div className="barlist-fill" style={{ width: `${Math.max((r.value / max) * 100, r.value > 0 ? 2 : 0)}%`, background: color }} /></div>
            </li>
          );
        })}
      </ol>
      {node}
    </ChartHost>
  );
}

/** A single percentage as an arc around its own number. Only for a genuine
 * share of a whole; an arc implies a maximum. */
export function Gauge({ percent, label, tone = "good", size = 108 }: { percent: number; label?: string; tone?: Tone; size?: number }) {
  const p = Math.max(0, Math.min(100, Number(percent) || 0));
  const stroke = 9;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  return (
    <div className="gauge" style={{ width: size, height: size }} role="img" aria-label={`${Math.round(p)}%${label ? " " + label : ""}`}>
      <svg width={size} height={size} aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--well)" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={TONE_VAR[tone]} strokeWidth={stroke} strokeLinecap="round" strokeDasharray={`${(p / 100) * c} ${c}`} data-percent={Math.round(p)} />
      </svg>
      <div className="gauge-text">
        <span className="gauge-value">{Math.round(p)}<span className="gauge-unit">%</span></span>
        {label && <span className="gauge-label">{label}</span>}
      </div>
    </div>
  );
}
